import { Preferences } from '@capacitor/preferences';
import clsx, { type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import i18n from './i18n';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { jwtDecode } from 'jwt-decode';
import React from 'react';
import { IonRouterLink } from '@ionic/react';
// Tremor
export const cx = (...args: ClassValue[]) => {
  return twMerge(clsx(...args));
}

export const hasErrorInput = [
  // base
  'ring-2',
  // border color
  'border-red-500 dark:border-red-700',
  // ring color
  'ring-red-200 dark:ring-red-700/30',
];
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
export function capitalizeFirstLetter(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

export async function getGeoIp() {
  const response = await fetch('http://ip-api.com/json/');

  const data = await response.json();

  return {
    lat: data.lat,
    lon: data.lon,
    city: data.city,
  }
}

export function capitalizeCityName(cityName: string): string {
  if (!cityName) {
    return ('[NULL TEXT]');
  }

  // List of words that should remain lowercase
  const lowercaseWords = ['on', 'the', 'and', 'of', 'in', 'at', 'for', 'with', 'et', 'de'];

  // Function to capitalize the first letter of a word
  const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

  // Process each part, handling hyphenated words separately
  return cityName
    .split(' ') // Split by spaces
    .map((word) => {
      // Handle hyphenated words like "Winchester-on-the-Severn"
      const hyphenatedWords = word.split('-');
      return hyphenatedWords
        .map(part => lowercaseWords.includes(part.toLowerCase()) ? part.toLowerCase() : capitalize(part))
        .join('-');
    })
    .join(' '); // Rejoin the words with spaces
}

export function languageToCountryFlag(locale: string) {
  if (locale === 'en') {
    return 'US';
  }
  if (locale === 'zh') {
    return 'CN';
  }
  if (locale === 'ja') {
    return 'JP';
  }

  if (locale === 'ko') {
    return 'KR';
  }

  if (locale === 'vi') {
    return 'VN';
  }
  return locale.toUpperCase();
}

export const getLocale = () => {
  return i18n.language ||
    Preferences.get({ key: 'locale' }).then(res => res.value) ||
    (typeof window !== 'undefined' && window.localStorage.i18nextLng) ||
    'en';
};

export const isDebugMode = () => {
  return process.env.NODE_ENV === 'development';
}
async function isOnboardingDone(token?: string) {
  if (!token) {
    token = (await FirebaseAuthentication.getIdToken()).token;
  }

  const decodedToken = jwtDecode(token) as { onboardingDone: boolean };

  if (decodedToken) {
    return decodedToken?.onboardingDone;
  }

  return false;

}
export function getAnimatedVideoThumbnailUrl(videoId: string): string {
  return videoId.replace('manifest/video.m3u8', 'thumbnails/thumbnail.gif?time=1s&height=600&duration=4s');
}

export function getVideoThumbnailUrl(videoId: string): string {
  return videoId.replace('manifest/video.m3u8', 'thumbnails/thumbnail.jpg');
}

/**
 * Renders text with hashtags highlighted in green and linked to their topic pages
 * @param text - The text content that may contain hashtags
 * @returns React elements with hashtags rendered as links
 */
export const renderCaptionWithHashtags = (text: string): React.ReactNode[] | null => {
  if (!text) return null;

  // Regex to match hashtags (word boundary followed by # and word characters)
  const hashtagRegex = /(#\w+)/g;

  // Split the text by hashtags and create an array of elements
  const parts = text.split(hashtagRegex);

  return parts.map((part, index) => {
    if (part.match(hashtagRegex)) {
      const hashtag = part.substring(1); // Remove the # symbol
      return React.createElement(
        IonRouterLink,
        {
          key: index,
          routerLink: `/discover/topic/${hashtag}`,
          className: "text-green-600 font-medium",
          onClick: (e: React.MouseEvent) => e.stopPropagation()
        },
        part
      );
    }
    return React.createElement(React.Fragment, { key: index }, part);
  });
};


async function convertDistance(valueInMeters: number): Promise<string> {
  const { value } = await Preferences.get({ key: 'isMetric' });
  const isMetric = value === 'true';

  if (isMetric) {
    return `${valueInMeters} m`;
  } else {
    const yards = valueInMeters * 1.09361;
    return `${yards.toFixed(1)} yd`;
  }
}

async function toMeters(valueInYards: number): Promise<number> {
  return valueInYards * 0.9144;
}

async function toYards(valueInMeters: number): Promise<number> {
  return valueInMeters * 1.09361;
}

// Helper function to get color with proper opacity
export function getTeeColor(colorName: string) {
  // Map localized color names to standard colors
  const colorLocaleAlias: Record<string, string[]> = {
    red: ["rouge", "rouges", "rot", "rojo", "rosso", "vermelho", "красный"],
    blue: ["bleu", "bleus", "blau", "azul", "blu", "синий"],
    white: ["blanc", "blancs", "weiss", "blanco", "bianco", "branco", "белый"],
    yellow: ["jaune", "jaunes", "gelb", "amarillo", "giallo", "amarelo", "желтый"],
    black: ["noir", "noirs", "schwarz", "negro", "nero", "preto", "черный"],
    green: ["vert", "verts", "grün", "verde", "зеленый"],
    gold: ["or", "doré", "dorés", "gold", "dorado", "oro", "ouro", "золотой"],
    silver: ["argent", "argenté", "argentés", "silber", "plateado", "argento", "prata", "серебряный"],
    orange: ["orangé", "orangés", "naranja", "arancione", "laranja", "оранжевый"],
    purple: ["violet", "violets", "pourpre", "lila", "púrpura", "viola", "roxo", "фиолетовый"],
  }

  // Standard color classes with dark mode support
  const colorMap: Record<string, string> = {
    red: "bg-red-100 text-red-800",
    blue: "bg-blue-100 text-blue-800",
    white: "bg-white border text-slate-800",
    yellow: "bg-amber-100 text-amber-800",
    black: "bg-black text-white border",
    green: "bg-emerald-100 text-emerald-800",
    gold: "bg-amber-100 text-amber-800",
    silver: "bg-gray-100 text-gray-800",
    orange: "bg-orange-100 text-orange-800",
    purple: "bg-purple-100 text-purple-800",
  }

  // Normalize the color name to lowercase for comparison
  const normalizedColor = colorName.toLowerCase().trim()

  // Check if the color name is a standard color
  if (colorMap[normalizedColor]) {
    return colorMap[normalizedColor]
  }

  // Check if the color name is an alias for a standard color
  for (const [standardColor, aliases] of Object.entries(colorLocaleAlias)) {
    if (aliases.includes(normalizedColor)) {
      return colorMap[standardColor]
    }
  }

  // Default fallback color
  return "bg-gray-100 dark:bg-gray-700/30 text-gray-800 dark:text-gray-200"
}